import { useNavigate } from 'react-router';

import {
  ArrowLeft,
  ArrowRight,
  Check,
  Reply,
  Save,
  Trash2,
  TriangleAlert,
} from 'lucide-react';

import { tabList } from '@/components/comments/CommentsPanel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';

export const DocumentActionsHead = ({ actions, documentStatus }) => {
  const navigate = useNavigate();

  const { mode } = useDetailMode(DETAIL_TYPE.DOCUMENT);

  const { showConfirmModal } = useConfirmModal();

  const handleDraftClick = () => {
    showConfirmModal({
      title: 'Save Draft',
      description:
        'Are you sure you want to save your changes as a draft and go back to the list?',
      onConfirm:
        mode === DETAIL_MODE.EDIT ? actions.update : actions.createDraft,
    });
  };

  const handleDeleteClick = () => {
    showConfirmModal({
      title: 'Delete Draft',
      description:
        'This action permanently deletes this draft and cannot be undone. Are you sure?',
      onConfirm: actions.deleteDraft,
      variant: 'destructive',
    });
  };

  return (
    <>
      <Button variant="outline" onClick={() => navigate(-1)}>
        <Reply className="mr-2 h-4 w-4" />
        Back
      </Button>
      {(mode === DETAIL_MODE.NEW ||
        (mode === DETAIL_MODE.EDIT && documentStatus === 'DRAFT')) && (
        <Button variant="outline" onClick={handleDraftClick}>
          <Save className="mr-2 h-4 w-4" />
          Save Draft
        </Button>
      )}
      {mode === DETAIL_MODE.EDIT && documentStatus === 'DRAFT' && (
        <Button
          className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
          variant="outline"
          onClick={handleDeleteClick}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Draft
        </Button>
      )}
    </>
  );
};

export const DocumentActionsTail = ({
  activeTab,
  setActiveTab,
  tabs,
  documentTitle,
  canApprove,
  reviews,
  actions,
  allowedActions,
  documentStatus,
  rejectReason,
  setRejectReason,
  dialogs: {
    isApproveDialogOpen,
    setIsApproveDialogOpen,
    isRejectDialogOpen,
    setIsRejectDialogOpen,
    isReturnDialogOpen,
    setIsReturnDialogOpen,
  },
  blockingCommentsCount = 0,
}) => {
  const { isMasterPreview, isSnapshotMode, mode } = useDetailMode(
    DETAIL_TYPE.DOCUMENT
  );

  const { showConfirmModal } = useConfirmModal();

  const isFirstTab = activeTab === 'information';
  const isLastTab = activeTab === 'approvals';

  const handlePreviousClick = () => {
    const idx = tabs.indexOf(activeTab);

    if (idx > 0) setActiveTab(tabs[idx - 1]);
  };

  const handleNext = () => {
    const idx = tabs.indexOf(activeTab);

    if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1]);
  };

  if (isMasterPreview || isSnapshotMode) return;

  const handleSubmit = () => {
    if (mode === DETAIL_MODE.NEW)
      showConfirmModal({
        title: 'Confirm Submission',
        description:
          'Are you sure you want to submit this form? This action cannot be undone.',
        onConfirm: actions.createDocument,
      });
    if (mode === DETAIL_MODE.EDIT)
      showConfirmModal({
        title: 'Confirm Submission',
        description:
          'Are you sure you want to submit this draft? This action cannot be undone.',
        onConfirm: actions.submitDraft,
      });
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        {mode === DETAIL_MODE.STATIC && (
          <>
            {allowedActions.includes('approve') &&
              blockingCommentsCount > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 text-xs text-amber-700 whitespace-nowrap">
                  <TriangleAlert className="shrink-0 h-3.5 w-3.5" />
                  <span>
                    {blockingCommentsCount} unresolved comment
                    {blockingCommentsCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            {allowedActions.includes('reject') && (
              <Button
                className="bg-red-500"
                onClick={() => setIsRejectDialogOpen(true)}
              >
                REJECT
              </Button>
            )}
            {allowedActions.includes('returnToRequestor') && (
              <Button
                className="bg-red-500"
                disabled={reviews.length === 0}
                onClick={() => setIsReturnDialogOpen(true)}
              >
                RETURN TO REQUESTOR
              </Button>
            )}
            {allowedActions.includes('approve') && (
              <Button
                disabled={!canApprove || blockingCommentsCount > 0}
                onClick={() => setIsApproveDialogOpen(true)}
              >
                APPROVE
              </Button>
            )}
            {allowedActions.includes('submit') && (
              <Button onClick={actions.submit}>SUBMIT FOR REVIEW</Button>
            )}
            {allowedActions.includes('newRevision') && (
              <Button variant="outline" onClick={actions.newRevision}>
                NEW REVISION
              </Button>
            )}
          </>
        )}
        {mode === DETAIL_MODE.NEW && (
          <>
            {!isFirstTab && (
              <Button
                className="align-top"
                onClick={handlePreviousClick}
                variant="outline"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
            )}
            {!isLastTab && (
              <Button onClick={handleNext} variant="outline">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </>
        )}
        {((mode === DETAIL_MODE.NEW && isLastTab) ||
          (mode === DETAIL_MODE.EDIT && documentStatus === 'DRAFT')) && (
          <Button onClick={handleSubmit}>
            Submit
            <Check className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      {/* ── Reject Dialog ───────────────────── */}
      <Dialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          setIsRejectDialogOpen(open);
          if (!open) setRejectReason('');
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
            <DialogDescription>
              You are about to reject the document:{' '}
              <span className="font-medium text-foreground">
                {documentTitle}
              </span>
              . Please provide a reason below.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium mb-1 block">
              Reason for Rejection
            </label>
            <Textarea
              className="font-mono resize-none"
              onChange={({ target }) => setRejectReason(target.value)}
              placeholder="Enter reason for rejection..."
              rows={4}
              value={rejectReason}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-500"
              disabled={!rejectReason.trim()}
              onClick={actions.reject}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Approve Dialog ──────────────────── */}
      <Dialog open={isApproveDialogOpen} onOpenChange={setIsApproveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Approve Document</DialogTitle>
            <DialogDescription>
              You are about to approve the document:{' '}
              <span className="font-medium text-foreground">
                {documentTitle}
              </span>
              . By confirming, you acknowledge that you have reviewed this
              document and agree with its contents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsApproveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={actions.approve}>Confirm Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Return to Requestor Dialog ──────── */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Return to Requestor</DialogTitle>
            <DialogDescription>
              The following comments will be sent to the document creator.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-3 py-2">
            {tabList
              .filter(({ key }) => reviews.some(({ tab }) => tab === key))
              .map(({ key, title }) => (
                <div key={key}>
                  <h4 className="text-sm font-bold mb-1">{title}</h4>
                  <ul className="space-y-1">
                    {reviews
                      .filter(({ tab }) => tab === key)
                      .map(({ id: reviewId, text }) => (
                        <li key={reviewId} className="text-sm">
                          - {text}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReturnDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-yellow-500 hover:bg-yellow-600"
              onClick={actions.returnToRequestor}
            >
              Confirm Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
